import posthog from 'posthog-js'

import { isAnalyticsConfigured } from './config'
import type { AnalyticsEventMap, AnalyticsEventName } from './events'

/**
 * Marks who produced the event.
 *
 * BR-38 makes the platform the authoritative source of funnel progress, which
 * is impossible while `apps/onboarding` does not exist. Stamping the emitter now
 * means that when the backend starts dual-writing to `OnboardingEvent`, its
 * events arrive as `source: 'server'` and the two eras stay separable — no
 * history has to be discarded to trust the newer numbers.
 */
const SOURCE = 'client'

/**
 * Records an event.
 *
 * A plain function rather than a hook: it is called from effects, from event
 * handlers, and from inside the async workspace-creation chain in
 * `useCreateWorkspace`. Threading a hook result through all three buys nothing.
 *
 * No-op when unconfigured, so call sites never guard.
 */
export const track = <N extends AnalyticsEventName>(
  name: N,
  properties: AnalyticsEventMap[N]
): void => {
  if (!isAnalyticsConfigured) return

  posthog.capture(name, { ...properties, source: SOURCE })
}

/**
 * Records an event that has to survive the page going away.
 *
 * `sendBeacon` with `send_instantly` bypasses the request queue: a batched
 * request is still sitting in memory when the tab closes, and a request fired
 * from `unload` is dropped by the browser outright. This is the only reliable
 * path for BR-39's exit events.
 */
export const trackBeacon = <N extends AnalyticsEventName>(
  name: N,
  properties: AnalyticsEventMap[N]
): void => {
  if (!isAnalyticsConfigured) return

  posthog.capture(
    name,
    { ...properties, source: SOURCE },
    { transport: 'sendBeacon', send_instantly: true }
  )
}
